import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, FileSearch, Loader2, Mail } from "lucide-react";
import { createDossierCheckout } from "@/lib/api";

export default function DossierRequestPage() {
  const [requesterEmail, setRequesterEmail] = useState("");
  const [billOrMeasure, setBillOrMeasure] = useState("");
  const [specificQuestions, setSpecificQuestions] = useState("");
  const [deadlineHint, setDeadlineHint] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const email = requesterEmail.trim();
    const target = billOrMeasure.trim();

    if (!email) {
      toast.error("Enter an email for dossier delivery.");
      return;
    }
    if (!target) {
      toast.error("Enter a bill, measure, proposition, or URL.");
      return;
    }

    setLoading(true);
    try {
      const data = await createDossierCheckout({
        requester_email: email,
        bill_or_measure: target,
        specific_questions: specificQuestions.trim() || undefined,
        deadline_hint: deadlineHint.trim() || undefined,
      });
      if (!data?.checkout_url) throw new Error("Missing checkout URL");
      window.location.href = data.checkout_url;
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Could not start dossier checkout.");
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10 sm:py-16" data-testid="dossier-page">
      <section className="mb-8">
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <span className="stamp stamp-implied">Custom research</span>
          <span className="stamp stamp-explicit">One-time order</span>
        </div>
        <h1 className="hero-title text-4xl sm:text-5xl text-[hsl(var(--ink))] mb-5">
          Request a custom Measure X-Ray dossier.
        </h1>
        <p className="text-base sm:text-lg text-[hsl(var(--ink-2))] max-w-2xl leading-relaxed">
          For bills, propositions, local measures, or URLs that need a deeper plain-English review.
        </p>
      </section>

      <div className="grid lg:grid-cols-[1fr_320px] gap-5 items-start">
        <form onSubmit={submit} className="paper-card-elevated rounded-sm p-5 sm:p-7" data-testid="dossier-form">
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="dossier-email" className="section-label block mb-2">
                Delivery email
              </label>
              <input
                id="dossier-email"
                type="email"
                required
                value={requesterEmail}
                onChange={(e) => setRequesterEmail(e.target.value)}
                data-testid="dossier-email-input"
                className="w-full bg-white border border-[hsl(var(--rule))] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-blue)/0.25)]"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label htmlFor="dossier-deadline" className="section-label block mb-2">
                Deadline hint
              </label>
              <input
                id="dossier-deadline"
                value={deadlineHint}
                onChange={(e) => setDeadlineHint(e.target.value)}
                data-testid="dossier-deadline-input"
                className="w-full bg-white border border-[hsl(var(--rule))] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-blue)/0.25)]"
                placeholder="before the election, next Tuesday, etc."
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="dossier-bill" className="section-label block mb-2">
              Bill or measure
            </label>
            <input
              id="dossier-bill"
              required
              value={billOrMeasure}
              onChange={(e) => setBillOrMeasure(e.target.value)}
              data-testid="dossier-bill-input"
              className="w-full bg-white border border-[hsl(var(--rule))] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-blue)/0.25)]"
              placeholder="AB 1234, Prop 36, local measure name, or URL"
            />
          </div>

          <div className="mb-5">
            <label htmlFor="dossier-questions" className="section-label block mb-2">
              Specific questions
            </label>
            <textarea
              id="dossier-questions"
              value={specificQuestions}
              onChange={(e) => setSpecificQuestions(e.target.value)}
              data-testid="dossier-questions-input"
              rows={6}
              className="w-full bg-white border border-[hsl(var(--rule))] rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--accent-blue)/0.25)]"
              placeholder="What are you most worried about? Hidden fiscal effects, current-law conflicts, who benefits, etc."
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="dossier-submit-button"
            className="pill-btn pill-btn-primary justify-center disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            Continue to $150 checkout
          </button>
        </form>

        <aside className="paper-card rounded-sm p-5" data-testid="dossier-pricing-card">
          <FileSearch className="w-6 h-6 text-[hsl(var(--accent-blue))] mb-3" />
          <div className="section-label mb-2">Pricing</div>
          <div className="font-serif text-4xl font-semibold mb-2">$150</div>
          <p className="text-sm text-[hsl(var(--ink-2))] leading-relaxed mb-4">
            One-time. Delivered as a PDF within 48 hours.
          </p>
          <div className="flex items-start gap-2 text-xs text-[hsl(var(--ink-muted))] leading-relaxed">
            <Mail className="w-4 h-4 shrink-0 mt-0.5" />
            <span>The email is required only because the custom report has to be delivered somewhere.</span>
          </div>
          <div className="mt-5 pt-4 border-t border-dashed border-[hsl(var(--rule))] text-xs text-[hsl(var(--ink-muted))]">
            Prefer to support the free public tool instead? <Link to="/support" className="ink-underline text-[hsl(var(--ink))]">Go to support</Link>.
          </div>
        </aside>
      </div>
    </div>
  );
}
